import { Result } from 'neverthrow';

import {
  GuardedLoadSubmitOptions,
  LoadBuildingGuardrailCustomer,
} from 'contexts/loadBuildingGuardrail';
import { CreateLoadRequest } from 'lib/api/createLoad';
import { Customer, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';

/** Thrown when the user dismisses the prod-TMS guardrail dialog without submitting. */
export class LoadBuildingGuardrailCancelled extends Error {
  constructor() {
    super('Load build cancelled');
    this.name = 'LoadBuildingGuardrailCancelled';
  }
}

export function isLoadBuildingGuardrailCancelled(
  error: unknown
): error is LoadBuildingGuardrailCancelled {
  return error instanceof LoadBuildingGuardrailCancelled;
}

/** Dialog stays open for retry; the outer guarded submit should stop awaiting. */
export class LoadBuildingGuardrailPendingRetry extends Error {
  constructor() {
    super('Load build awaiting guardrail retry');
    this.name = 'LoadBuildingGuardrailPendingRetry';
  }
}

export function isLoadBuildingGuardrailPendingRetry(
  error: unknown
): error is LoadBuildingGuardrailPendingRetry {
  return error instanceof LoadBuildingGuardrailPendingRetry;
}

/** Dialog already surfaced this failure via toast; forms must not toast again. */
export class LoadBuildingGuardrailAlreadyNotified extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoadBuildingGuardrailAlreadyNotified';
  }
}

export function isLoadBuildingGuardrailAlreadyNotified(
  error: unknown
): error is LoadBuildingGuardrailAlreadyNotified {
  return error instanceof LoadBuildingGuardrailAlreadyNotified;
}

/** User-facing message when guarded submit fails with an unexpected client error. */
export const GUARDED_CREATE_LOAD_ERROR_MESSAGE = 'Unable to submit load.';

/** Guarded create-load should return null (clear loading) without treating as user cancel. */
export function shouldAbortGuardedCreateLoad(error: unknown): boolean {
  return (
    isLoadBuildingGuardrailCancelled(error) ||
    isLoadBuildingGuardrailPendingRetry(error) ||
    isLoadBuildingGuardrailAlreadyNotified(error)
  );
}

/** True when the user dismissed the prod-TMS guardrail dialog without submitting. */
export function isGuardedCreateLoadAborted<T>(
  result: Result<T, ApiError> | null
): result is null {
  return result === null;
}

function tmsCustomerMatchesGuardrailIdentity(
  tmsCustomer: TMSCustomer,
  guardCustomer: LoadBuildingGuardrailCustomer
): boolean {
  const ext = guardCustomer.externalTMSID?.trim();
  if (ext && tmsCustomer.externalTMSID != null) {
    return String(tmsCustomer.externalTMSID).trim() === ext;
  }
  if (guardCustomer.id != null) {
    return (
      tmsCustomer.id === guardCustomer.id || tmsCustomer.ID === guardCustomer.id
    );
  }
  return false;
}

export function toGuardrailCustomer(
  customer?: Customer | TMSCustomer | LoadBuildingGuardrailCustomer
): LoadBuildingGuardrailCustomer | undefined {
  if (!customer) {
    return undefined;
  }
  const tmsCustomer = customer as TMSCustomer;
  const id =
    tmsCustomer.id ??
    tmsCustomer.ID ??
    (customer as LoadBuildingGuardrailCustomer).id;
  return {
    id,
    externalTMSID: customer.externalTMSID,
    name: customer.name,
    isTest:
      (customer as LoadBuildingGuardrailCustomer).isTest ??
      (customer as Customer).isTest ??
      tmsCustomer.isTest,
  };
}

/** Merge Drumkit DB id from the form; prefer TMS list for isTest when matched. */
export function resolveGuardrailCustomer(
  customer: Customer | TMSCustomer | LoadBuildingGuardrailCustomer | undefined,
  tmsCustomers?: Maybe<TMSCustomer[]>
): LoadBuildingGuardrailCustomer | undefined {
  const guardCustomer = toGuardrailCustomer(customer);
  if (!guardCustomer || !tmsCustomers?.length) {
    return guardCustomer;
  }

  const match = tmsCustomers.find((c) =>
    tmsCustomerMatchesGuardrailIdentity(c, guardCustomer)
  );
  if (!match) {
    return guardCustomer;
  }

  const dbId = match.id ?? match.ID;
  return {
    ...guardCustomer,
    id: guardCustomer.id ?? dbId,
    name: guardCustomer.name || match.name,
    externalTMSID: guardCustomer.externalTMSID ?? match.externalTMSID,
    isTest: match.isTest ?? guardCustomer.isTest,
  };
}

/** Applies one-time prod TMS override flag to a standard create-load request. */
export function withProdTMSOverride(
  req: CreateLoadRequest,
  opts: GuardedLoadSubmitOptions
): CreateLoadRequest {
  if (!opts.acknowledgeProdTMSOverride) {
    return req;
  }
  return {
    ...req,
    metadata: {
      ...req.metadata,
      isPickupDateOnly: req.metadata?.isPickupDateOnly ?? false,
      isDropoffDateOnly: req.metadata?.isDropoffDateOnly ?? false,
      acknowledgeProdTMSOverride: true,
    },
  };
}
